import React, { memo, useCallback, useMemo } from "react"

import { useHumanFeedbackUpdate } from "@/human-feedback/annotations"
import {
  getCompareByDisplayLabel,
  getCompareByGroupKeys,
  groupMetricOutputsByCompareBy,
  type CompareBy,
} from "@/metrics/compare-by"
import {
  CategoricalAnnotationSelector,
  getMetricColorForMetric,
  MiniCompareBarChart,
  yesNoBadgeColorClasses,
  yesNoBadgeShapeClasses,
} from "@/metrics/metric-display"
import { MetricRecord } from "@/metrics/runtime"
import type { Annotation } from "@/types"
import { useUser } from "@clerk/nextjs"
import { Tooltip } from "@coval/ui"

import type { ChartBarFilter } from "@/types/ChartBarFilter"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import type { RunRowLike } from "@/types/RunRow"
import { formatMetricDisplayName } from "@/utils/formatting/format-metric-display-results"

import { AggregateOverviewSet } from "../../components/aggregate/AggregateOverviewSet"
import { AggregateOverviewString } from "../../components/aggregate/AggregateOverviewString"
import { SingleString } from "../../components/single/SingleString"

// Stable component wrapper for categorical human review input
interface CategoricalHumanReviewInputWrapperProps {
  metricId: string
  simulationOutputId: string
  annotationsForMetric: Annotation[]
  projectId?: string
  categories: string[]
  categoryColors?: Record<string, string>
}

const CategoricalHumanReviewInputWrapper: React.FC<CategoricalHumanReviewInputWrapperProps> =
  memo(
    ({
      metricId,
      simulationOutputId,
      annotationsForMetric,
      projectId,
      categories,
      categoryColors,
    }: CategoricalHumanReviewInputWrapperProps) => {
      const { user } = useUser()

      const userEmail = user?.emailAddresses?.[0]?.emailAddress

      const myAnnotation = useMemo(() => {
        return annotationsForMetric?.find(
          (a: Annotation) => a.assignee === userEmail,
        )
      }, [annotationsForMetric, userEmail])

      const { currentAnnotation, handleValueUpdate } = useHumanFeedbackUpdate({
        simulationOutputId,
        metricId,
        initialAnnotation: myAnnotation || null,
        projectId,
      })

      const handleValueChange = useCallback(
        (value: string | null) => {
          void handleValueUpdate(null, value, null)
        },
        [handleValueUpdate],
      )

      return (
        <CategoricalAnnotationSelector
          selectedValue={currentAnnotation?.ground_truth_string_value}
          onValueChange={handleValueChange}
          metricId={metricId}
          categories={categories}
          categoryColors={categoryColors}
        />
      )
    },
  )
CategoricalHumanReviewInputWrapper.displayName =
  "CategoricalHumanReviewInputWrapper"

/**
 * Type base class for named-category string output metrics.
 *
 * Distinct from BinaryMetricRecord: categories are open-ended names
 * (e.g. sentiment labels, end reasons), not YES/NO/UNKNOWN. No
 * N/A→UNKNOWN normalization, no binary percentage header.
 */
export class CategoricalMetricRecord extends MetricRecord {
  // ─── Comparison ──────────────────────────────────────────────────────────

  compareValues(
    value1: { string_value?: string | null },
    value2: { string_value?: string | null },
  ): boolean {
    return (
      value1.string_value?.toLowerCase() === value2.string_value?.toLowerCase()
    )
  }

  // ─── Badge ────────────────────────────────────────────────────────────────

  _renderSimulationCard(mo: RunMetricOutput): React.ReactNode {
    const color = getMetricColorForMetric(mo.string_value ?? -1, this)
    const displayValue = formatMetricDisplayName(mo.string_value ?? "--")

    // Use the same chip style as non-robotic tone detection for consistency
    return (
      <Tooltip content={displayValue}>
        <span
          className={`${yesNoBadgeShapeClasses} ${yesNoBadgeColorClasses[color] ?? yesNoBadgeColorClasses.default}`}
        >
          {displayValue}
        </span>
      </Tooltip>
    )
  }

  // ─── Single output ────────────────────────────────────────────────────────

  getSingleMetricOutputNode(mo: RunMetricOutput): React.ReactNode | null {
    return <SingleString mo={mo} />
  }

  // ─── Overview ─────────────────────────────────────────────────────────────

  getComparedOverviewNode(
    runRows: RunRowLike[],
    compareBy: CompareBy,
  ): React.ReactNode | null {
    const allGroupKeys = getCompareByGroupKeys(runRows, compareBy)
    if (!runRows.length || allGroupKeys.length === 0) {
      return null
    }
    const mo = this._getMetricOutputsFromRunRows(runRows)
    const groups = groupMetricOutputsByCompareBy(mo, runRows, compareBy)
    const data = allGroupKeys.map((key) => {
      const groupMo = groups.get(key) ?? []
      const label = getCompareByDisplayLabel(key, compareBy)
      return {
        id: key,
        label,
        value: groupMo.length,
        color: compareBy.colors[key],
      }
    })
    return (
      <MiniCompareBarChart
        key={`${compareBy.dimension}-${compareBy.metadataKey ?? ""}`}
        data={data}
        valueLabel="Count"
        formatValue={(v) => String(Math.round(v))}
      />
    )
  }

  getOverviewNode(mo: RunMetricOutput[]): React.ReactNode | null {
    if (!mo[0]) {
      return null
    }
    if (mo[0].output_type === "set") {
      return <AggregateOverviewSet mos={mo} />
    }
    return <AggregateOverviewString mos={mo} />
  }

  renderInDepthRunReview(
    metricOutputs: RunMetricOutput[],
    context?: {
      runRows?: RunRowLike[]
      highlightValue?: number | string | null
      onBarClick?: (filter: { type: "exact"; value: string }) => void
      activeFilters?: ChartBarFilter[]
    },
  ): React.ReactNode {
    if (metricOutputs.length === 0) {
      return (
        <div className="text-coval-text-muted">
          No metric data available for this metric.
        </div>
      )
    }
    if (!metricOutputs[0]) {
      return null
    }
    if (metricOutputs[0].output_type === "set") {
      return (
        <div className="w-full">
          <AggregateOverviewSet
            mos={metricOutputs}
            onBarClick={context?.onBarClick}
            activeFilters={context?.activeFilters}
          />
        </div>
      )
    }
    return (
      <div className="w-full">
        <AggregateOverviewString
          mos={metricOutputs}
          onBarClick={context?.onBarClick}
          activeFilters={context?.activeFilters}
        />
      </div>
    )
  }

  // ─── Human review ───────────────────────────────────────────────────────

  getCategoricalOptions(): string[] {
    return (
      this.metric_metadata?.additional_information?.value
        ?.split(",")
        .map((value) => value.trim())
        .filter((value) => value.length > 0) ?? []
    )
  }

  getCategoryColors(): Record<string, string> | undefined {
    return undefined
  }

  getKeyboardShortcutHints(): { key: string; label: string }[] {
    const categories = this.getCategoricalOptions()

    if (categories.length === 0) {
      return []
    }

    return [
      { key: "1-9", label: "Select category" },
      { key: "Enter", label: "Dropdown" },
    ]
  }

  renderAssignmentsTableReviewCell(
    simulationOutputId: string,
    annotationsForMetric: Annotation[],
    projectId?: string,
  ): React.ReactNode {
    const categories = this.getCategoricalOptions()

    if (categories.length === 0) {
      return "-"
    }
    if (!this.id) {
      return "-"
    }

    return (
      <CategoricalHumanReviewInputWrapper
        metricId={this.id}
        simulationOutputId={simulationOutputId}
        annotationsForMetric={annotationsForMetric}
        projectId={projectId}
        categories={categories}
        categoryColors={this.getCategoryColors()}
      />
    )
  }
}
